// BookDemoPage.jsx
//
// The demo-request capture form. Two-column layout on desktop:
//   Left  — value-prop reassurance: what happens after they submit,
//            who they'll meet with, how soon, what to expect.
//   Right — the form itself.
//
// Mobile: single column, reassurance above the form.
//
// On submit:
//   * Pre-validate client-side (required fields + email format)
//   * POST to public.demo_requests via submitDemoRequest()
//   * On success: swap the form panel for a thank-you state with two
//                 next-step CTAs (Read about us / Back home)
//   * On failure: inline error, form stays filled in

import React, { useState } from 'react';
import {
  ArrowRight, Calendar, CheckCircle2, Clock, Loader, MessageCircle,
  Shield, Mail, Phone, Building2, User as UserIcon, Briefcase, AlertCircle,
} from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';
import { submitDemoRequest } from '../../lib/db';

// ─── Form options ──────────────────────────────────────────────────

const FIRM_SIZES = [
  { value: '1-5',   label: '1–5 staff (solo / small)' },
  { value: '6-20',  label: '6–20 staff' },
  { value: '21-50', label: '21–50 staff' },
  { value: '50+',   label: '50+ staff (enterprise)' },
];

const ROLES = [
  { value: 'founder',  label: 'Founder / Partner' },
  { value: 'admin',    label: 'Practice Manager / Admin' },
  { value: 'it',       label: 'IT' },
  { value: 'attorney', label: 'Attorney / Associate' },
  { value: 'other',    label: 'Other' },
];

const TIMES = [
  { value: 'morning',   label: 'Morning (9 AM – 12 PM)' },
  { value: 'afternoon', label: 'Afternoon (1 – 5 PM)' },
  { value: 'evening',   label: 'Evening (after 5 PM)' },
  { value: 'anytime',   label: 'Anytime — whenever works for you' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ─── Page ──────────────────────────────────────────────────────────

export default function BookDemoPage() {
  const [form, setForm] = useState({
    fullName:      '',
    email:         '',
    phone:         '',
    firmName:      '',
    firmSize:      '',
    role:          '',
    practiceArea:  '',
    preferredTime: '',
    message:       '',
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [done,       setDone]       = useState(false);

  // ─── Validation ────────────────────────────────────────────────
  const fieldError = (field) => {
    if (!touched[field]) return '';
    switch (field) {
      case 'fullName': return form.fullName.trim()  ? '' : 'Required';
      case 'email':
        if (!form.email.trim())     return 'Required';
        if (!EMAIL_RE.test(form.email.trim())) return 'That doesn\'t look like a valid email';
        return '';
      case 'firmName': return form.firmName.trim()  ? '' : 'Required';
      default:         return '';
    }
  };

  const isValid =
    form.fullName.trim() && form.email.trim() && EMAIL_RE.test(form.email.trim()) && form.firmName.trim();

  // Diagnostic: when set, shown in a collapsible "Technical details"
  // section below the friendly error message. Helps us debug deployment
  // issues without exposing scary error messages to end users.
  const [errorDetail, setErrorDetail] = useState('');

  // Maps a Supabase / PostgREST error to a user-friendly message.
  // Surfacing the actual error.code / message helps us debug deployment
  // issues (most commonly: migration not applied yet) without making
  // every visitor stare at "relation public.demo_requests does not exist".
  const friendlyError = (dbErr) => {
    const msg  = (dbErr?.message || '').toLowerCase();
    const code = (dbErr?.code    || '').toUpperCase();

    // Table doesn't exist — migration 149 hasn't been applied to this
    // Supabase project yet. We can't fix this from the client; we just
    // need to tell the user (and ourselves) what's wrong.
    if (
      code === 'PGRST205' ||
      code === '42P01' ||
      (msg.includes('relation') && msg.includes('does not exist')) ||
      msg.includes('could not find the table') ||
      msg.includes("could not find the 'demo_requests'")
    ) {
      return 'Our demo-request form isn\'t fully set up yet. Please email hello@docketflowapp.com directly and we\'ll get back to you within a business day.';
    }

    // RLS rejected the insert — policy missing or misconfigured.
    if (code === '42501' || msg.includes('row-level security') || msg.includes('rls')) {
      return 'Our demo-request form has a permissions issue we need to fix. Please email hello@docketflowapp.com directly and we\'ll respond within a business day.';
    }

    // CHECK constraint violation — bad input the visitor can fix.
    if (msg.includes('check constraint')) {
      return 'Please double-check your name, email, and firm name and try again.';
    }

    // Network-ish failures (CORS, offline, etc.).
    if (msg.includes('failed to fetch') || msg.includes('network')) {
      return 'Could not reach our servers. Please check your internet connection and try again.';
    }

    // Generic fallback.
    return 'Something went wrong sending your request. Please try again, or email hello@docketflowapp.com directly.';
  };

  // ─── Submit ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ fullName: true, email: true, firmName: true });
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    setErrorDetail('');
    try {
      const { error: dbErr } = await submitDemoRequest(form);
      if (dbErr) {
        // Always log the full error so we can debug deployment issues
        // from the browser console — even if the visitor only sees a
        // friendly message.
        // eslint-disable-next-line no-console
        console.warn('[BookDemo] submission failed:', dbErr);
        setError(friendlyError(dbErr));
        setErrorDetail(
          `[${dbErr.code || 'no-code'}] ${dbErr.message || 'no message'}`
          + (dbErr.hint    ? `\nHint: ${dbErr.hint}`       : '')
          + (dbErr.details ? `\nDetails: ${dbErr.details}` : ''),
        );
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[BookDemo] submission threw:', err);
      setError('Could not reach our servers. Please check your internet connection and try again.');
      setErrorDetail(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <main className="min-h-[calc(100vh-200px)] py-12 lg:py-20" style={{ background: BRAND.bgLight }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-14 items-start">

          {/* ─── Left: reassurance column ─── */}
          <ReassurancePanel />

          {/* ─── Right: form OR thank-you ─── */}
          <div
            className="rounded-2xl border p-7 lg:p-9"
            style={{
              background: BRAND.bgWhite,
              borderColor: BRAND.border,
              boxShadow: '0 16px 56px rgba(15,42,95,0.08)',
            }}
          >
            {done ? (
              <ThankYou form={form} />
            ) : (
              <Form
                form={form}
                setForm={setForm}
                touched={touched}
                setTouched={setTouched}
                fieldError={fieldError}
                isValid={isValid}
                submitting={submitting}
                error={error}
                errorDetail={errorDetail}
                handleSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────────────
// REASSURANCE PANEL — what to expect
// ────────────────────────────────────────────────────────────────────

function ReassurancePanel() {
  return (
    <div className="lg:sticky lg:top-24">
      <p
        className="text-xs font-bold uppercase tracking-widest mb-3"
        style={{ color: BRAND.orangeDark }}
      >
        Book a demo
      </p>
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5"
        style={{ color: BRAND.textHead }}
      >
        See how DocketFlow fits your firm.
      </h1>
      <p className="text-base sm:text-lg leading-relaxed mb-8" style={{ color: BRAND.textMuted }}>
        Fill in a few details and we'll send you a calendar invite for a 20-minute
        walkthrough — tailored to the way your firm actually practices.
      </p>

      <div className="space-y-5">
        {[
          {
            icon: Clock,
            title: 'Quick reply',
            sub:   'We respond within one business day with a calendar link.',
          },
          {
            icon: MessageCircle,
            title: 'No hard sell',
            sub:   'A real conversation, not a 60-slide deck. Bring your questions.',
          },
          {
            icon: Calendar,
            title: '20 minutes is enough',
            sub:   'We tailor the demo to your practice — civil, criminal, corporate, family, or mixed.',
          },
          {
            icon: Shield,
            title: 'Your data, locked down',
            sub:   'Everything you share is stored on our isolated tenant infrastructure, encrypted in transit.',
          },
        ].map((item) => {
          const Ic = item.icon;
          return (
            <div key={item.title} className="flex items-start gap-3.5">
              <div
                className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
              >
                <Ic size={17} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: BRAND.textHead }}>
                  {item.title}
                </p>
                <p className="text-xs mt-0.5 leading-relaxed" style={{ color: BRAND.textMuted }}>
                  {item.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="mt-9 p-4 rounded-xl"
        style={{ background: BRAND.bgWhite, border: `1px solid ${BRAND.border}` }}
      >
        <p className="text-xs leading-relaxed" style={{ color: BRAND.textMuted }}>
          <strong style={{ color: BRAND.textHead }}>Prefer email?</strong>
          {' '}Reach us at{' '}
          <a
            href="mailto:hello@docketflowapp.com"
            className="font-semibold"
            style={{ color: BRAND.blueDeep }}
          >
            hello@docketflowapp.com
          </a>
          {' '}and we'll get back to you.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// FORM
// ────────────────────────────────────────────────────────────────────

function Form({
  form, setForm, touched, setTouched, fieldError, isValid,
  submitting, error, errorDetail, handleSubmit,
}) {
  const upd = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const blur = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <h2 className="text-xl font-bold" style={{ color: BRAND.textHead }}>
        Tell us about your firm
      </h2>

      {/* Name + Email */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          icon={UserIcon}
          label="Full name"
          required
          value={form.fullName}
          onChange={upd('fullName')}
          onBlur={blur('fullName')}
          error={fieldError('fullName')}
          placeholder="Ama Owusu"
          autoComplete="name"
        />
        <Field
          icon={Mail}
          label="Work email"
          required
          type="email"
          value={form.email}
          onChange={upd('email')}
          onBlur={blur('email')}
          error={fieldError('email')}
          placeholder="ama@yourfirm.com"
          autoComplete="email"
        />
      </div>

      {/* Phone + Firm */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          icon={Phone}
          label="Phone"
          value={form.phone}
          onChange={upd('phone')}
          placeholder="+233 ..."
          autoComplete="tel"
          hint="Optional"
        />
        <Field
          icon={Building2}
          label="Firm name"
          required
          value={form.firmName}
          onChange={upd('firmName')}
          onBlur={blur('firmName')}
          error={fieldError('firmName')}
          placeholder="Owusu & Co."
          autoComplete="organization"
        />
      </div>

      {/* Firm size + Role */}
      <div className="grid sm:grid-cols-2 gap-4">
        <SelectField
          label="Firm size"
          value={form.firmSize}
          onChange={upd('firmSize')}
          options={FIRM_SIZES}
          placeholder="Select size"
        />
        <SelectField
          label="Your role"
          value={form.role}
          onChange={upd('role')}
          options={ROLES}
          placeholder="Select role"
        />
      </div>

      {/* Practice area + Preferred time */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          icon={Briefcase}
          label="Practice area"
          value={form.practiceArea}
          onChange={upd('practiceArea')}
          placeholder="Civil, Criminal, Corporate ..."
          hint="Optional"
        />
        <SelectField
          label="Preferred demo time"
          value={form.preferredTime}
          onChange={upd('preferredTime')}
          options={TIMES}
          placeholder="When works for you?"
        />
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: BRAND.textHead }}>
          Anything specific you'd like to see?
          <span className="ml-2 text-xs font-normal" style={{ color: BRAND.textFaint }}>
            Optional
          </span>
        </label>
        <textarea
          value={form.message}
          onChange={upd('message')}
          rows={4}
          maxLength={4000}
          placeholder="Trust accounting, conflict checks, client portal ..."
          className="w-full px-3.5 py-3 text-sm rounded-lg border focus:outline-none focus:ring-2 transition"
          style={{
            borderColor: BRAND.border,
            background: BRAND.bgWhite,
            color: BRAND.textBody,
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = BRAND.blueUi; }}
          onBlurCapture={(e) => { e.currentTarget.style.borderColor = BRAND.border; }}
        />
      </div>

      {/* Error — friendly message on top, expandable technical detail
          underneath so curious / IT-savvy users can self-diagnose
          without us having to ask them to open DevTools. */}
      {error && (
        <div
          className="flex items-start gap-2.5 p-3.5 rounded-lg text-sm"
          style={{ background: 'rgba(239,68,68,0.06)', color: '#B91C1C', border: '1px solid rgba(239,68,68,0.18)' }}
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p>{error}</p>
            {errorDetail && (
              <details className="mt-2">
                <summary
                  className="text-xs font-semibold cursor-pointer select-none"
                  style={{ color: '#9A1A1A' }}
                >
                  Technical details
                </summary>
                <pre
                  className="mt-1.5 px-2.5 py-2 rounded-md text-[10.5px] font-mono leading-snug whitespace-pre-wrap break-all"
                  style={{ background: 'rgba(239,68,68,0.10)', color: '#7F1D1D' }}
                >
                  {errorDetail}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Submit — intentionally NOT disabled when invalid. A disabled
          button gives no feedback about WHY it won't work; letting the
          click through and rendering inline errors next to the offending
          fields is the more discoverable UX. The button is only disabled
          while a request is in flight, to prevent double-submits. */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-xl transition-all"
          style={{
            background: submitting
              ? BRAND.bgLighter
              : `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
            color: submitting ? BRAND.textFaint : '#fff',
            boxShadow: submitting ? 'none' : '0 8px 24px rgba(249,115,22,0.35)',
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? (
            <>
              <Loader size={16} className="animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Request my demo
              <ArrowRight size={16} />
            </>
          )}
        </button>
        <p className="text-xs text-center mt-3" style={{ color: BRAND.textFaint }}>
          By submitting, you agree to be contacted by the DocketFlow team about your request.
        </p>
      </div>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────
// FIELDS
// ────────────────────────────────────────────────────────────────────

function Field({
  icon: Ic, label, value, onChange, onBlur, error, hint, required,
  placeholder, type = 'text', autoComplete,
}) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2" style={{ color: BRAND.textHead }}>
        {label}
        {required && <span className="ml-1" style={{ color: BRAND.orangeDark }}>*</span>}
        {!required && hint && (
          <span className="ml-2 text-xs font-normal" style={{ color: BRAND.textFaint }}>
            {hint}
          </span>
        )}
      </label>
      <div className="relative">
        {Ic && (
          <Ic
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: BRAND.textFaint }}
          />
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border focus:outline-none transition"
          style={{
            borderColor: error ? '#EF4444' : BRAND.border,
            background: BRAND.bgWhite,
            color: BRAND.textBody,
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = error ? '#EF4444' : BRAND.blueUi; }}
          onBlurCapture={(e) => { e.currentTarget.style.borderColor = error ? '#EF4444' : BRAND.border; }}
        />
      </div>
      {error && (
        <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#B91C1C' }}>
          <AlertCircle size={11} />
          {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2" style={{ color: BRAND.textHead }}>
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        className="w-full px-3.5 py-2.5 text-sm rounded-lg border focus:outline-none transition appearance-none bg-no-repeat"
        style={{
          borderColor: BRAND.border,
          background: `${BRAND.bgWhite} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%2364748B'%3E%3Cpath d='M4.5 6L8 9.5 11.5 6h-7z'/%3E%3C/svg%3E") right 0.75rem center/14px no-repeat`,
          color: value ? BRAND.textBody : BRAND.textFaint,
          paddingRight: '2.25rem',
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = BRAND.blueUi; }}
        onBlurCapture={(e) => { e.currentTarget.style.borderColor = BRAND.border; }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// THANK-YOU
// ────────────────────────────────────────────────────────────────────

function ThankYou({ form }) {
  return (
    <div className="text-center py-6">
      <div
        className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
        style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
      >
        <CheckCircle2 size={30} />
      </div>
      <h2 className="text-2xl font-bold mb-3" style={{ color: BRAND.textHead }}>
        Thank you, {form.fullName.split(' ')[0] || 'there'}!
      </h2>
      <p className="text-base leading-relaxed max-w-md mx-auto mb-7" style={{ color: BRAND.textMuted }}>
        We've received your request. A member of our team will email{' '}
        <strong style={{ color: BRAND.textHead }}>{form.email}</strong>{' '}
        within one business day with a calendar link.
      </p>
      <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-md mx-auto">
        <button
          onClick={() => goTo('about')}
          className="flex-1 px-5 py-3 text-sm font-semibold rounded-xl"
          style={{ background: BRAND.bgLight, color: BRAND.blueDeep, border: `1px solid ${BRAND.border}` }}
        >
          Read about us
        </button>
        <button
          onClick={() => goTo('home')}
          className="flex-1 px-5 py-3 text-sm font-semibold rounded-xl"
          style={{ background: BRAND.blueDeep, color: '#fff' }}
        >
          Back to home
        </button>
      </div>
    </div>
  );
}
